<script setup>
import { MenuItem, MenuItems } from '@headlessui/vue'
import { useCalendar } from '@daysmart/daysmart-vue-components/composables'
import { useCalendarViewState } from '../composables/useCalendarViewState'
import { useCalendarSettings } from '../composables/useCalendarSettings'

const props = defineProps({
  date: {
    type: String,
    required: true,
  },
})
const emit = defineEmits(['selectDate'])
const dayjs = useDayjs()
const view = useCalendarViewState()
const { t } = useI18n()
const {
  isWidget,
  widgetSettingsOptionsType,
  widgetSettingsOptionsSpecificPages,
  widgetSettingsOptionsView,
} = useWidgetSettings()
const { customFirstDayOfWeek } = useCalendarSettings()
const { getWeekView, getMonthView } = useCalendar({ customFirstDayOfWeek })

const listOptions = computed(() => {
  return [
    { id: 'month', name: t('Month') },
    { id: 'week', name: t('Week') },
    { id: 'day', name: t('Day') },
  ]
})

const dropdownLabel = computed(() => `${listOptions.value.find(e => e.id === view.value)?.name}`)
const todaysDate = computed(() => dayjs.value().format('YYYY-MM-DD'))
const isTodaysDate = computed(() => props.date === todaysDate.value)
const disableTodayButton = computed(() => {
  if (view.value === 'day' && isTodaysDate.value)
    return true
  if (view.value === 'week') {
    const weekView = getWeekView.value(props.date)
    return weekView.some(day => day.isSame(todaysDate.value, 'day'))
  }
  if (view.value === 'month') {
    const monthView = getMonthView.value(props.date)
    return monthView.some(day => day.isSame(todaysDate.value, 'day'))
  }
  return false
})

function isActive(viewKey) {
  return viewKey === view.value
}

function getActiveClass(viewKey) {
  return isActive(viewKey) ? '!bg-secondary-400 !text-text-100' : ''
}

const useWidgetPageVisibility = isWidget && widgetSettingsOptionsType === 'specific_pages'
function isViewVisibleBasedOnSpecificPages(viewKey) {
  return !useWidgetPageVisibility || widgetSettingsOptionsSpecificPages.includes(`schedule.${viewKey}`)
}
</script>

<template>
  <div class="mb-4">
    <div class="sm:(flex flex-wrap justify-center flex-gap-y-2) lt-sm:hidden">
      <button
        class="mr-2 btn-sm px-4 py-2 btn-alt-outline-secondary"
        :disabled="disableTodayButton"
        @click="emit('selectDate', todaysDate)"
      >
        {{ t('Today') }}
      </button>
      <div class="flex">
        <DSButton
          v-if="isViewVisibleBasedOnSpecificPages('month')"
          v-slot="{ focus, hover }"
          class="view-button"
          :class="getActiveClass('month')"
          @click="view = 'month'"
        >
          <DSIcon
            :icon="[(focus || hover || isActive('month')) ? 'i-fas-calendar-days' : 'i-fal-calendar-days']"
          />
          {{ t('Month') }}
        </DSButton>
        <DSButton
          v-if="isViewVisibleBasedOnSpecificPages('week')"
          v-slot="{ focus, hover }"
          class="view-button"
          :class="getActiveClass('week')"
          @click="view = 'week'"
        >
          <DSIcon
            :icon="[(focus || hover || isActive('week')) ? 'i-fas-calendar-week' : 'i-fal-calendar-week']"
          />
          {{ t('Week') }}
        </DSButton>
        <DSButton
          v-if="isViewVisibleBasedOnSpecificPages('day')"
          v-slot="{ focus, hover }"
          class="view-button"
          :class="getActiveClass('day')"
          @click="view = 'day'"
        >
          <DSIcon
            :icon="[(focus || hover || isActive('day')) ? 'i-fas-calendar-week' : 'i-fal-calendar-week']"
          />
          {{ t('Day') }}
        </DSButton>
      </div>
    </div>
    <!-- for mobile -->
    <div class="sm:(w-full hidden)">
      <DSDropdown
        class="w-full b-1 border-surface-700 rounded-4px bg-white px-4 py-2 !justify-center"
        menu-items-classes="right-0 rounded-4px"
        omit-position-relative
        placement="bottom"
      >
        <template #label>
          <span class="text-sm text-text-500 fw-400">{{ dropdownLabel }}</span>
        </template>
        <template #default="{ close }">
          <MenuItems @click="close">
            <MenuItem
              v-for="(option, index) of listOptions"
              v-slot="{ active }"
              :key="index"
            >
              <button
                class="w-240px flex items-center px-4 py-2 text-sm text-text-500"
                :class="{ 'bg-surface-500': active }"
                @click="view = option.id"
              >
                {{ option?.name }}
              </button>
            </MenuItem>
          </MenuItems>
        </template>
      </DSDropdown>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.view-button {
  @apply first:(b-r-0 important-b-l-1 rounded-l-6px) last:(important-b-r-1 b-l-0 rounded-r-6px) flex items-center flex-gap-x-1 b-1 border-surface-700 px-4 py-2 important-ring-0 btn-alt-outline-secondary-group;
}
</style>

<i18n>
{
  "en-us": {
    "Today": "Today",
    "Month": "Month",
    "Week": "Week",
    "Day": "Day"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "Today": "Heute",
  "Month": "Monat",
  "Week": "Woche",
  "Day": "Tag"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "Today": "Aujourd'hui",
  "Month": "Mois",
  "Week": "Semaine",
  "Day": "Jour"
}
</i18n>

<i18n locale="es" lang="json">
{
  "Today": "Hoy",
  "Month": "Mes",
  "Week": "Semana",
  "Day": "Día"
}
</i18n>
